import { HttpErrorResponse } from "@angular/common/http";

export interface LookupMetadataDomain {
  catalogTypeCode: string;
  domainCode: string;
  displayName: string;
  description?: string | null;
  configurationCode: string;
  entryCount: number;
}

export interface LookupMetadataDomainDetail extends LookupMetadataDomain {
  entries: LookupMetadataEntry[];
  configurationVersions?: LookupMetadataConfigurationVersion[] | null;
}
export interface LookupMetadataConfigurationVersion {
  id: number;
  configurationCode: string;
  versionCode: string;
  displayName?: string | null;
  statusCode: string;
  effectiveStartUtc: string;
  effectiveEndUtc?: string | null;
}

export interface LookupMetadataEntry {
  id: number;
  catalogTypeCode: string;
  domainCode: string;
  configurationCode: string;
  versionCode: string;
  configurationVersionId: number;
  versionStatusCode: string;
  code: string;
  displayText: string;
  isActive: boolean;
  effectiveStartUtc: string;
  effectiveEndUtc?: string | null;
  scopeTypeCode: string;
  scopeKey?: string | null;
  sortOrder: number;
  metadata: Record<string, string | null | undefined>;
}

export interface LookupMetadataEntryCatalogRow extends Omit<LookupMetadataEntry, "metadata"> {
  catalogEntryKey: string;
  entityCode?: string | null;
  requiredModeCode?: string | null;
  visibleModeCode?: string | null;
  editableModeCode?: string | null;
  ruleCode?: string | null;
  clientHintCode?: string | null;
  categoryCode?: string | null;
  severityCode?: string | null;
  clientHandlingHintCode?: string | null;
  subjectCode?: string | null;
  bindingTypeCode?: string | null;
  expressionJson?: string | null;
  isDefault?: boolean | null;
}

export interface LookupMetadataEntryUpsertRequest {
  catalogTypeCode: string;
  domainCode: string;
  configurationVersionId: number;
  code: string;
  displayText: string;
  isActive: boolean;
  effectiveStartUtc: string;
  effectiveEndUtc?: string | null;
  scopeTypeCode: string;
  scopeKey?: string | null;
  sortOrder: number;
  metadata: Record<string, string | null>;
}

export interface LookupMetadataFormValues {
  configurationVersionId: number | string | null;
  code: string;
  displayText: string;
  isActive: boolean;
  effectiveStartUtc: string | Date | null;
  effectiveEndUtc: string | Date | null;
  scopeTypeCode: string;
  scopeKey: string;
  sortOrder: number | string | null;
  entityCode: string;
  requiredModeCode: string;
  visibleModeCode: string;
  editableModeCode: string;
  ruleCode: string;
  clientHintCode: string;
  categoryCode: string;
  severityCode: string;
  clientHandlingHintCode: string;
  subjectCode: string;
  bindingTypeCode: string;
  expressionJson: string;
  isDefault: boolean;
}

export interface LookupMetadataDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  problemCode?: string;
  issueCodes: string[];
  correlationId?: string | null;
}

export function mapLookupMetadataAdminError(error: unknown): LookupMetadataDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const messages = new Set<string>();
    Object.values(problem.errors ?? {}).flat().forEach((message) => {
      if (typeof message === "string" && message.trim()) {
        messages.add(message.trim());
      }
    });

    if (typeof problem.detail === "string" && problem.detail.trim()) {
      messages.add(problem.detail.trim());
    }

    if (messages.size === 0) {
      messages.add(defaultMessage(error.status));
    }

    return {
      title: normalizeText(problem.title) || defaultTitle(error.status),
      messages: [...messages],
      statusCode: error.status || problem.status,
      problemCode: normalizeText(problem.problemCode) || undefined,
      issueCodes: Array.isArray(problem.issueCodes)
        ? problem.issueCodes.filter((code): code is string => normalizeText(code).length > 0)
        : [],
      correlationId: error.headers?.get("X-Correlation-ID"),
    };
  }

  return {
    title: "Unexpected error",
    messages: ["The request could not be completed. Try again or contact support if the problem persists."],
    issueCodes: [],
  };
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  problemCode?: string;
  issueCodes?: string[];
  errors?: Record<string, string[] | string>;
}

function toProblemDetails(errorBody: unknown): ProblemDetails {
  return errorBody && typeof errorBody === "object" && !Array.isArray(errorBody)
    ? errorBody as ProblemDetails
    : {};
}

function defaultTitle(statusCode: number): string {
  switch (statusCode) {
    case 401:
      return "Sign-in required";
    case 403:
      return "Access denied";
    case 404:
      return "Catalog entry not found";
    case 409:
      return "Conflict detected";
    default:
      return "Request could not be completed";
  }
}

function defaultMessage(statusCode: number): string {
  switch (statusCode) {
    case 401:
      return "Your session is no longer authenticated. Sign in again and retry the action.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested catalog or entry could not be found.";
    case 409:
      return "The requested change conflicts with the current catalog state.";
    default:
      return "Review the values and try again.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
